import { Printer } from "lucide-react";
import { Link } from "@/lib/nav";

import Banner from "@buildpy/ui/banner";
import FilterChips from "@/components/filter-chips";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";

import Money from "@/components/money";
import {
  STATEMENT_GROUPING_LABEL,
  STATEMENT_GROUPINGS,
  statementPath,
  statementPrintPath,
} from "@buildpy/core/modules/change-order/current-statement";
import CurrentStatementCards from "@/modules/change-order/ui/current-statement-cards";
import CurrentStatementTable from "@/modules/change-order/ui/current-statement-table";
import type { ContractStatementView } from "@buildpy/core/views/g2a/contract-statement";
import PageHeader from "@/shell/page-header";

// 현재 내역서 — 원 계약 견적 줄에 승인된 변경공사를 줄 단위로 얹어 지금 계약이 무엇인지 보인다(읽기 전용).
const Page = ({ data }: { data: ContractStatementView }) => {
  const { contract, grouping, result } = data;
  if (!result.ok) {
    return (
      <>
        <PageHeader
          meta={
            <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/contracts/${contract.id}`}>
              계약 {contract.contract_no} · {contract.title}
            </Link>
          }
          title="현재 내역서"
        />
        <Banner tone="warning">{result.message}</Banner>
      </>
    );
  }
  const { statement, changeOrders } = result.loaded;
  const pending = changeOrders.filter((co) => co.status === "DRAFT" || co.status === "SENT").length;

  return (
    <>
      <PageHeader
        actions={
          <Button asChild size="sm" variant="outline">
            <Link href={statementPrintPath(contract.id, grouping)}>
              <Printer />
              인쇄
            </Link>
          </Button>
        }
        meta={
          <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/contracts/${contract.id}`}>
            계약 {contract.contract_no} · {contract.title}
          </Link>
        }
        title="현재 내역서"
      />

      <div className="flex flex-col gap-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <FilterChips
            activeHref={statementPath(contract.id, grouping)}
            aria-label="묶음"
            items={STATEMENT_GROUPINGS.map((g) => ({ label: STATEMENT_GROUPING_LABEL[g], href: statementPath(contract.id, g) }))}
          />
          <p className="hidden text-sm tabular-nums md:block print:block" data-testid="current-statement-summary">
            현재 계약금액 <Money className="font-semibold" value={statement.totals.total.current} />
            <span className="text-muted-foreground"> · 원 계약 <Money value={statement.totals.total.original} /></span>
          </p>
        </div>

        {(statement.columns.length === 0 || pending > 0) && (
          <SectionCard as="div" className="text-sm text-muted-foreground" padding="sm" data-testid="current-statement-note">
            {statement.columns.length === 0 ? "승인된 변경공사가 없어 원 계약 내역만 보입니다." : "승인된 변경공사만 반영합니다."}
            {pending > 0 && ` 작성 중·승인 대기 ${pending}건은 승인되면 여기에 더해집니다.`}
          </SectionCard>
        )}

        <SectionCard className="hidden md:block print:block" padding="sm">
          <CurrentStatementTable statement={statement} />
        </SectionCard>
        <CurrentStatementCards head={{ title: contract.title, contractNo: contract.contract_no }} statement={statement} />
      </div>
    </>
  );
};

export default Page;
